import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./ImageGallery.scss";
import classnamesBind from "classnames/bind";
import useTouchControl from "../../hooks/useTouchControl";
import getWindow from "../../utils/window";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useImageGalleryContext } from "./ImageGalleryContext";
import resetClippedContainerScroll from "../../utils/resetClippedContainerScroll";
import {
  ImageGalleryComponentProps,
  GalleryImage,
} from "../../types/components/imageGallery";

const classnames = classnamesBind.bind(styles);

function getChildNodes(
  elements: NodeListOf<ChildNode> | ChildNode[],
): ChildNode[] {
  const arr = Array.from(elements);
  if (arr.some((element) => element.childNodes.length)) {
    return arr
      .filter((element) => element.childNodes.length)
      .flatMap((element) => [
        ...Array.from(element.childNodes),
        ...getChildNodes(element.childNodes),
      ]);
  }
  return [];
}

interface ImageIndexArgs {
  previousImageIndex: number;
  displayedImageIndex: number;
  lastImageIndex: number;
}

function movingFirstToLastImage({
  previousImageIndex,
  displayedImageIndex,
  lastImageIndex,
}: ImageIndexArgs): boolean {
  return previousImageIndex === 0 && displayedImageIndex === lastImageIndex;
}

function movingLastToFirstImage({
  previousImageIndex,
  displayedImageIndex,
  lastImageIndex,
}: ImageIndexArgs): boolean {
  return previousImageIndex === lastImageIndex && displayedImageIndex === 0;
}

export default function ImageGallery({
  screenHoverable,
  mapImage,
  fullHeightContainer = true,
}: ImageGalleryComponentProps): JSX.Element | null {
  const {
    images,
    displayedImageIndex,
    setDisplayedImageIndex,
    isSwipeDisabled,
    shouldAnimateTransition,
    resumeTransitions,
    goToNext,
    goToPrevious,
  } = useImageGalleryContext();
  const shouldRenderSingleImage = images?.length === 1;
  const imageListLength = images.length;
  const totalItems = imageListLength + 2;
  const itemWidthPercent = 100 / totalItems;

  const [touchStartPosition, setTouchStartPosition] = useState<number | null>(
    null,
  );
  const paginationButtonRefs = useRef<(HTMLElement | ChildNode)[]>([]);
  const chevronButtonRefs = useRef<(HTMLElement | ChildNode)[]>([]);
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const scrollStartPosition = useRef(0);
  const swipeDisabledAtTouchStart = useRef(false);
  const initialRender = useRef(true);
  const previousIndex = useRef(displayedImageIndex);
  const lastImageIndex = imageListLength - 1;
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string, params?: Record<string, unknown>) => string;
  };

  const getGalleryPosition = useCallback(
    (index: number): string =>
      `translateX(-${(index + 1) * itemWidthPercent}%)`,
    [itemWidthPercent],
  );

  useEffect(() => {
    const previousImageIndex = previousIndex.current;
    previousIndex.current = displayedImageIndex;

    const setGalleryContainerPosition = ({
      startAtClone = true,
      index = displayedImageIndex,
    }: { startAtClone?: boolean; index?: number } = {}) => {
      if (shouldRenderSingleImage || !galleryContainerRef.current) {
        return;
      }
      if (startAtClone) {
        galleryContainerRef.current.style.transform = getGalleryPosition(index);
      } else {
        galleryContainerRef.current.style.transform = `translateX(${-index * itemWidthPercent}%)`;
      }

      resumeTransitions();
    };

    if (shouldAnimateTransition || initialRender.current) {
      setGalleryContainerPosition();
      return;
    }

    if (
      movingLastToFirstImage({
        previousImageIndex,
        displayedImageIndex,
        lastImageIndex,
      })
    ) {
      setGalleryContainerPosition({ startAtClone: false, index: 0 });
      return;
    }

    if (
      movingFirstToLastImage({
        previousImageIndex,
        displayedImageIndex,
        lastImageIndex,
      })
    ) {
      setGalleryContainerPosition({ index: lastImageIndex + 1 });
    }
  }, [
    shouldAnimateTransition,
    displayedImageIndex,
    lastImageIndex,
    shouldRenderSingleImage,
    getGalleryPosition,
    itemWidthPercent,
    resumeTransitions,
  ]);

  useEffect(() => {
    initialRender.current = false;
  }, []);

  const setPaginationButtonRefs = (element: HTMLButtonElement | null) => {
    if (element) {
      paginationButtonRefs.current.push(element);
      paginationButtonRefs.current.push(...getChildNodes(element.childNodes));
    }
  };
  const setChevronButtonRefs = (element: HTMLButtonElement | null) => {
    if (element) {
      chevronButtonRefs.current.push(element);
      chevronButtonRefs.current.push(...getChildNodes(element.childNodes));
    }
  };

  const paginationButtons = () => {
    paginationButtonRefs.current = [];
    chevronButtonRefs.current = [];
    return images.map((image: GalleryImage, index: number) => {
      const selected = displayedImageIndex === index;
      return (
        <li key={`${image.url}-${index}`}>
          <button
            ref={setPaginationButtonRefs}
            className={classnames(styles.paginationButtons)}
            onClick={() => setDisplayedImageIndex(index)}
            aria-current={selected}
            aria-label={getTranslation(
              "pdp_ratings_review_gallery_image_count",
              {
                index: index + 1,
                total: imageListLength,
              },
            )}
          >
            <div className={classnames(styles.circleButtons, { selected })} />
          </button>
        </li>
      );
    });
  };

  const displayChevrons = () => {
    paginationButtonRefs.current = [];
    chevronButtonRefs.current = [];
    return (
      <div key="chevrons" data-testid="imageGalleryChevrons">
        <button
          className={classnames(styles.chevron, styles.nextButton)}
          onClick={goToNext}
          aria-label={getTranslation("pdp_media_gallery_carousel_next")}
          ref={setChevronButtonRefs}
        >
          <div className={classnames("product-chevron-right-large-outlined")} />
        </button>
        <button
          className={classnames(styles.chevron, styles.previousButton)}
          onClick={goToPrevious}
          aria-label={getTranslation("pdp_media_gallery_carousel_previous")}
          ref={setChevronButtonRefs}
        >
          <div className={classnames("product-chevron-left-large-outlined")} />
        </button>
      </div>
    );
  };

  const SWIPE_THRESHOLD_PX = 10;
  const SCROLL_THRESHOLD_PX = 10;

  const getScrollPosition = () => getWindow()?.scrollY ?? 0;

  const touchStartHandler = (event: TouchEvent) => {
    swipeDisabledAtTouchStart.current = isSwipeDisabled;
    scrollStartPosition.current = getScrollPosition();
    setTouchStartPosition(
      event.touches.length === 1 ? event.changedTouches[0].clientX : null,
    );
  };

  const shouldFireTouchEvent = (event: TouchEvent) => {
    if (swipeDisabledAtTouchStart.current || isSwipeDisabled) {
      return false;
    }

    const scrollDistance = Math.abs(
      getScrollPosition() - scrollStartPosition.current,
    );

    if (scrollDistance >= SCROLL_THRESHOLD_PX) {
      return false;
    }

    const clickableElements = [
      ...paginationButtonRefs.current,
      ...chevronButtonRefs.current,
    ];

    if (
      clickableElements.includes(
        (event as unknown as { srcElement: HTMLElement }).srcElement,
      ) ||
      touchStartPosition === null
    ) {
      return false;
    }

    const endTouchPosition = event.changedTouches[0].clientX;
    return (
      Math.abs(endTouchPosition - touchStartPosition) >= SWIPE_THRESHOLD_PX
    );
  };

  const touchEndHandler = (event: TouchEvent) => {
    if (shouldFireTouchEvent(event)) {
      const endTouchPosition = event.changedTouches[0].clientX;
      const direction = endTouchPosition - touchStartPosition!;

      direction <= 0 ? goToNext() : goToPrevious();
    }

    setTouchStartPosition(null);
  };

  useTouchControl({
    startHandler: touchStartHandler,
    endHandler: touchEndHandler,
    elementRef: galleryContainerRef,
  });

  const renderGallery = () => {
    const itemStyle = { width: `${itemWidthPercent}%` };

    return (
      <div
        className={classnames(styles.galleryWrapper, {
          fullHeightContainer,
        })}
        data-testid="imageGallery"
        onScroll={resetClippedContainerScroll}
      >
        <div
          ref={galleryContainerRef}
          className={classnames(styles.galleryContainer, {
            animated: shouldAnimateTransition,
            fullHeightContainer,
          })}
          style={{
            width: `${totalItems * 100}%`,
            transform: getGalleryPosition(displayedImageIndex),
          }}
        >
          <div
            className={styles.galleryItem}
            style={itemStyle}
            aria-hidden="true"
            onScroll={resetClippedContainerScroll}
          >
            {mapImage({
              image: images[imageListLength - 1],
              index: imageListLength - 1,
              displayedImageIndex,
              ariaHiddenOverride: true,
            })}
          </div>
          {images.map((image: GalleryImage, index: number) => (
            <div
              className={styles.galleryItem}
              key={`${image.url}-${index}`}
              style={itemStyle}
              aria-hidden={index !== displayedImageIndex}
              onScroll={resetClippedContainerScroll}
            >
              {mapImage({ image, index, displayedImageIndex })}
            </div>
          ))}
          <div
            className={styles.galleryItem}
            style={itemStyle}
            aria-hidden="true"
            onScroll={resetClippedContainerScroll}
          >
            {mapImage({
              image: images[0],
              index: 0,
              displayedImageIndex,
              ariaHiddenOverride: true,
            })}
          </div>
        </div>
        {screenHoverable ? (
          displayChevrons()
        ) : (
          <ul
            key="pagination"
            className={classnames(styles.paginationButtonsContainer)}
            aria-label={getTranslation("pdp_media_gallery_select_image")}
            data-testid="imageGalleryPagination"
          >
            {paginationButtons()}
          </ul>
        )}
      </div>
    );
  };

  return images?.length ? (
    shouldRenderSingleImage ? (
      <div
        className={classnames(styles.imageContainer, {
          fullHeightContainer,
        })}
      >
        {mapImage({
          image: images[0],
          index: 0,
          displayedImageIndex,
        })}
      </div>
    ) : (
      renderGallery()
    )
  ) : null;
}
